/**
 * Platform detection utility
 * Detects if we're running in React Native or web environment
 */
export function isReactNative() {
    return typeof navigator !== "undefined" && navigator.product === "ReactNative";
}
export function isWeb() {
    return !isReactNative() && typeof window !== "undefined";
}
// Shared in-memory storage fallback (used when AsyncStorage is not available)
const memoryStorage = {};
/**
 * Get the appropriate storage implementation based on platform
 */
function getStorage() {
    if (isWeb() && typeof window !== "undefined" && window.sessionStorage) {
        return window.sessionStorage;
    }
    else if (isReactNative()) {
        // Dynamically import AsyncStorage for React Native
        // This will only work if @react-native-async-storage/async-storage is installed
        try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const AsyncStorage = require("@react-native-async-storage/async-storage").default;
            return {
                getItem: (key) => AsyncStorage.getItem(key),
                setItem: (key, value) => AsyncStorage.setItem(key, value),
                removeItem: (key) => AsyncStorage.removeItem(key),
            };
        }
        catch (error) {
            console.warn("[getStorage] AsyncStorage not available, falling back to in-memory storage");
            // Fallback to in-memory storage if AsyncStorage is not available
            return {
                getItem: (key) => Promise.resolve(memoryStorage[key] || null),
                setItem: (key, value) => {
                    memoryStorage[key] = value;
                    return Promise.resolve();
                },
                removeItem: (key) => {
                    delete memoryStorage[key];
                    return Promise.resolve();
                },
            };
        }
    }
    else {
        // Fallback to in-memory storage for unknown environments
        return {
            getItem: (key) => memoryStorage[key] || null,
            setItem: (key, value) => {
                memoryStorage[key] = value;
            },
            removeItem: (key) => {
                delete memoryStorage[key];
            },
        };
    }
}
/**
 * Get the sessionStorage key for write tokens for a given platform
 * @param platformId - The platform ID (bigint)
 * @returns The sessionStorage key string
 */
export function getWriteTokensKey(platformId) {
    return `write_tokens_${platformId?.toString()}`;
}
/**
 * Derive a cryptographic key from user-specific data using PBKDF2
 * This creates a consistent key per origin/user-agent combination
 */
async function deriveKey() {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const salt = new TextEncoder().encode(origin + userAgent);
    // Import password material (user-agent + origin) as a key
    const passwordKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(userAgent + origin), { name: "PBKDF2" }, false, ["deriveKey"]);
    // Derive a key using PBKDF2
    return crypto.subtle.deriveKey({
        name: "PBKDF2",
        salt,
        iterations: 100000, // High iteration count for security
        hash: "SHA-256",
    }, passwordKey, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}
/**
 * Encrypt a token using AES-GCM encryption
 * This provides strong cryptographic security
 */
async function encryptToken(token) {
    try {
        const key = await deriveKey();
        const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
        const encodedToken = new TextEncoder().encode(token);
        const encrypted = await crypto.subtle.encrypt({
            name: "AES-GCM",
            iv,
        }, key, encodedToken);
        // Combine IV and encrypted data, then base64 encode
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encrypted), iv.length);
        return btoa(String.fromCharCode(...combined));
    }
    catch (error) {
        console.error("[encryptToken] Failed to encrypt token:", error);
        throw new Error("Failed to encrypt token");
    }
}
/**
 * Decrypt a token that was encrypted with encryptToken
 */
async function decryptToken(encryptedToken) {
    try {
        const key = await deriveKey();
        const combined = Uint8Array.from(atob(encryptedToken), (c) => c.charCodeAt(0));
        // Extract IV (first 12 bytes) and encrypted data (rest)
        const iv = combined.slice(0, 12);
        const encrypted = combined.slice(12);
        const decrypted = await crypto.subtle.decrypt({
            name: "AES-GCM",
            iv,
        }, key, encrypted);
        return new TextDecoder().decode(decrypted);
    }
    catch (error) {
        console.error("[decryptToken] Failed to decrypt token:", error);
        throw new Error("Failed to decrypt token");
    }
}
/**
 * Store encrypted tokens in storage using AES-GCM encryption
 * Uses sessionStorage on web, AsyncStorage on React Native
 * This provides strong cryptographic security against XSS attacks.
 * Note: While this is cryptographically secure, determined attackers with
 * access to your JavaScript code can still decrypt. For maximum security,
 * consider server-side token storage.
 */
export async function storeEncryptedTokens(platformId, tokens) {
    const key = getWriteTokensKey(platformId);
    const storage = getStorage();
    try {
        const encrypted = await encryptToken(tokens);
        await storage.setItem(key, encrypted);
    }
    catch (error) {
        console.error("[storeEncryptedTokens] Failed to encrypt tokens:", error);
        // Fallback to unencrypted storage if encryption fails
        // This ensures the app continues to work even if crypto fails
        await storage.setItem(key, tokens);
    }
}
export async function removeEncryptedTokens(platformId) {
    const storage = getStorage();
    await storage.removeItem(getWriteTokensKey(platformId));
}
/**
 * Retrieve and decrypt tokens from storage
 * Uses sessionStorage on web, AsyncStorage on React Native
 * Returns null if tokens don't exist or decryption fails
 */
export async function getEncryptedTokens(platformId) {
    const key = getWriteTokensKey(platformId);
    const storage = getStorage();
    const encrypted = await storage.getItem(key);
    if (!encrypted) {
        return null;
    }
    try {
        return await decryptToken(encrypted);
    }
    catch (error) {
        console.error("[getEncryptedTokens] Failed to decrypt tokens:", error);
        // Try to read as unencrypted (for backward compatibility with old tokens)
        // Check if it looks like JSON (unencrypted) vs base64 (encrypted)
        try {
            JSON.parse(encrypted);
            return encrypted; // It's unencrypted JSON
        }
        catch {
            // Not valid JSON, probably corrupted encrypted data
            return null;
        }
    }
}
